/* ==========================================================================
   HOTTLE — LANDING PAGE behaviour. Loaded only on `lp-*` templates.

   Three jobs, all of which hottle.js deliberately does NOT do:

   1. Lazy, self-pausing demo videos. The page carries up to eight muted
      loops; autoplaying all of them on load would cost megabytes on the 4G
      phones this store lives on, and eight decoding video elements is what
      actually janks a mid-range Android. Each clip loads when it is close to
      the viewport, plays while it is on screen, and pauses when it leaves.
   2. Offer options across MORE THAN ONE buy box. hottle.js's initOffer binds
      `document.querySelector('[data-ht-offer]')` — the first one. The LP has
      a buy box in the middle and a closing offer at the end, and a shopper
      who picks "2 bucăți" at the top must not find "1 bucată" selected at the
      bottom. Every scope is bound and every scope is kept in sync.
   3. A sticky bar that hides again over the closing offer, so the shortcut
      never covers the button it is a shortcut to.

   No libraries, no build step. Every init is wrapped so one failure cannot
   take the page down with it, exactly like hottle.js.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Nothing on this page autoplays any more, so the old reduced-motion branch
  // for video is gone with it: click-to-play is already the accessible
  // behaviour everyone gets. REDUCED still governs scrolling and transitions.

  /* --- 1 · Players, click to start --------------------------------------
     Nothing loads until it is asked for. The page can carry a dozen clips and
     still cost nothing on first paint: no <source>, no iframe, no YouTube
     player script and no YouTube cookie until a visitor presses play. That
     one decision is worth more to this page's speed than every other
     optimisation on it put together.

     On press the facade is replaced in place — a real <video controls> for an
     uploaded file, a nocookie iframe for YouTube — and given focus, so a
     keyboard user lands on the player they just opened rather than back at
     the top of the document. */
  function initPlayers() {
    document.addEventListener('click', function (ev) {
      var btn = ev.target.closest('[data-lp-play]');
      if (!btn) return;

      var frame = btn.closest('.lp-media__frame');
      if (!frame) return;
      var kind = btn.getAttribute('data-kind');

      if (kind === 'youtube') {
        var id = btn.getAttribute('data-yt');
        if (!id) return;
        var iframe = document.createElement('iframe');
        iframe.className = 'lp-media__embed';
        iframe.setAttribute('src',
          'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) +
          '?autoplay=1&rel=0&modestbranding=1&playsinline=1');
        iframe.setAttribute('title', btn.getAttribute('aria-label') || 'Video');
        iframe.setAttribute('allow',
          'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; web-share');
        iframe.setAttribute('allowfullscreen', 'allowfullscreen');
        iframe.setAttribute('loading', 'lazy');
        frame.appendChild(iframe);
        btn.remove();
        iframe.focus();
        return;
      }

      var src = btn.getAttribute('data-src');
      if (!src) return;
      var v = document.createElement('video');
      v.className = 'lp-media__video';
      v.setAttribute('controls', 'controls');
      v.setAttribute('playsinline', 'playsinline');
      v.setAttribute('preload', 'auto');
      var source = document.createElement('source');
      source.setAttribute('src', src);
      var type = btn.getAttribute('data-type');
      if (type) source.setAttribute('type', type);
      v.appendChild(source);
      frame.appendChild(v);
      btn.remove();
      v.focus();
      var p = v.play();
      if (p && p.catch) p.catch(function () { /* the controls are there either way */ });
    });
  }

  /* --- 2 · Offer options, every scope in step ---------------------------- */
  function initOffers() {
    var scopes = [].slice.call(document.querySelectorAll('[data-lp-offer]'));
    if (!scopes.length) return;

    function paint(value) {
      // Re-queried on every change rather than closed over: the theme editor
      // re-renders one section at a time, so the scope captured at boot can be
      // a detached node while its replacement sits unbound in the document.
      var scopes = [].slice.call(document.querySelectorAll('[data-lp-offer]'));
      // Every rung describes itself on its own radio — where it goes, what the
      // button then says, what the bar quotes. This used to be a ternary on
      // "is it 2?", which is why a third rung could not exist without editing
      // JS, and why the wording lived in two places and drifted. Now nothing
      // here knows how many rungs there are or what any of them are called.
      var src = null;
      scopes.forEach(function (scope) {
        var radio = scope.querySelector('input[data-lp-qty="' + value + '"]');
        if (!radio) return;
        if (!src) src = radio;
        if (!radio.checked) radio.checked = true;

        // There is no cart step any more: the button IS a checkout permalink
        // carrying the chosen quantity, so switching option rewrites the href.
        var href = radio.getAttribute('data-checkout');
        var cta = scope.querySelector('[data-lp-checkout]');
        if (cta && href) cta.setAttribute('href', href);

        var label = cta && cta.querySelector('span');
        var wording = radio.getAttribute('data-cta');
        if (label && wording) label.textContent = wording;
      });

      // the bar quotes whichever option is selected, so the two can never
      // show different prices
      var bar = document.querySelector('[data-lp-sticky]');
      if (!bar || !src) return;
      var price = bar.querySelector('[data-lp-sticky-price]');
      var note = bar.querySelector('[data-lp-sticky-note]');
      var btn = bar.querySelector('[data-lp-sticky-submit]');
      var blabel = btn && btn.querySelector('span');
      var barHref = src.getAttribute('data-checkout');
      if (btn && barHref) btn.setAttribute('href', barHref);
      if (price && src.getAttribute('data-price')) price.textContent = src.getAttribute('data-price');
      if (note && src.getAttribute('data-note')) note.textContent = src.getAttribute('data-note');
      if (blabel && src.getAttribute('data-cta')) blabel.textContent = src.getAttribute('data-cta');
    }

    scopes.forEach(function (scope) {
      if (scope.dataset.lpBound === '1') return;
      scope.dataset.lpBound = '1';
      scope.addEventListener('change', function (e) {
        var r = e.target.closest('input[data-lp-qty]');
        if (!r) return;
        paint(r.getAttribute('data-lp-qty'));
      });
    });
  }

  /* --- 3 · Sticky bar ---------------------------------------------------- */
  function initSticky() {
    var bar = document.querySelector('[data-lp-sticky]');
    if (!bar || bar.dataset.lpBound === '1') return;
    var anchors = [].slice.call(document.querySelectorAll('[data-lp-atc-anchor]'));
    if (!anchors.length || !('IntersectionObserver' in window)) return;
    bar.dataset.lpBound = '1';

    var first = anchors[0];
    var last = anchors[anchors.length - 1];
    var state = { passedFirst: false, onLast: false };

    function apply() {
      var show = state.passedFirst && !state.onLast;
      bar.setAttribute('data-visible', show ? 'true' : 'false');
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.target === first) {
          // scrolled UP past the first buy box, not merely off-screen below it
          state.passedFirst = !e.isIntersecting && e.boundingClientRect.top < 0;
        }
        if (e.target === last) {
          state.onLast = e.isIntersecting;
        }
      });
      apply();
    }, { threshold: 0 });

    io.observe(first);
    if (last !== first) io.observe(last);

    // No click handler: the bar is an <a> to the checkout permalink and the
    // href is kept in step by paint(). A JS shim in front of a working link
    // is one more thing that can break between the tap and the checkout.
  }

  /* --- 4 · Video rail arrows --------------------------------------------
     Progressive enhancement only. The rail scrolls by touch, by trackpad and
     by keyboard without any of this; the buttons start `hidden` and are only
     un-hidden when there is somewhere to go, so a control that cannot do
     anything never appears. */
  function initRails() {
    [].slice.call(document.querySelectorAll('[data-lp-vrail]')).forEach(function (shell) {
      if (shell.dataset.lpBound === '1') return;
      shell.dataset.lpBound = '1';

      var track = shell.querySelector('[data-lp-vrail-track]');
      var prev = shell.querySelector('[data-lp-vrail-prev]');
      var next = shell.querySelector('[data-lp-vrail-next]');
      if (!track) return;
      // Dots live outside the shell, next to it, so look in the same section.
      var dots = shell.parentElement
        ? [].slice.call(shell.parentElement.querySelectorAll('[data-lp-vrail-dots] .lp-vrail__dot'))
        : [];

      function step() {
        var first = track.firstElementChild;
        // one card plus one gap, so a click lands the next card on the edge
        return first ? first.getBoundingClientRect().width + 14 : track.clientWidth * 0.8;
      }

      function sync() {
        var max = track.scrollWidth - track.clientWidth;
        // 2px of slack: sub-pixel layout means scrollLeft rarely hits max exactly
        if (prev) prev.hidden = track.scrollLeft <= 2;
        if (next) next.hidden = track.scrollLeft >= max - 2;

        if (!dots.length) return;
        // Which card is nearest the left edge. Measured off the real first
        // child rather than a constant, because the card width is a vw unit
        // and changes with the viewport and with the breakpoint.
        var first = track.firstElementChild;
        var w = first ? first.getBoundingClientRect().width + 14 : 1;
        var i = w > 0 ? Math.round(track.scrollLeft / w) : 0;
        // The last card can never reach the left edge — the track stops
        // scrolling first — so pin the final dot once we are at the end, or
        // it would be unreachable however far you flick.
        if (track.scrollLeft >= max - 2) i = dots.length - 1;
        if (i < 0) i = 0;
        if (i > dots.length - 1) i = dots.length - 1;
        dots.forEach(function (d, k) { d.classList.toggle('is-on', k === i); });
      }

      if (prev) prev.addEventListener('click', function () {
        track.scrollBy({ left: -step(), behavior: REDUCED ? 'auto' : 'smooth' });
      });
      if (next) next.addEventListener('click', function () {
        track.scrollBy({ left: step(), behavior: REDUCED ? 'auto' : 'smooth' });
      });

      var tick;
      track.addEventListener('scroll', function () {
        window.clearTimeout(tick);
        tick = window.setTimeout(sync, 80);
      }, { passive: true });

      window.addEventListener('resize', sync);
      // The observer's first report is the initial sync — no forced layout at boot.
      if ('ResizeObserver' in window) new ResizeObserver(sync).observe(track);
      else sync();
    });
  }

  /* --- 5 · Product shot -------------------------------------------------
     Thumbnails swap the stage image. Progressive enhancement: without JS the
     stage still shows the lead photo and the thumbnails are simply buttons
     that do nothing visible — which is why they are <button>s carrying the
     source as data rather than links that would navigate away from the offer. */
  function initShots() {
    [].slice.call(document.querySelectorAll('[data-lp-shot]')).forEach(function (shot) {
      if (shot.dataset.lpBound === '1') return;
      shot.dataset.lpBound = '1';

      var main = shot.querySelector('[data-lp-shot-main]');
      var thumbs = [].slice.call(shot.querySelectorAll('[data-lp-shot-thumb]'));
      if (!main || !thumbs.length) return;

      thumbs.forEach(function (t) {
        t.addEventListener('click', function () {
          var src = t.getAttribute('data-src');
          if (!src || main.getAttribute('src') === src) return;
          main.setAttribute('src', src);
          var ss = t.getAttribute('data-srcset');
          if (ss) main.setAttribute('srcset', ss);
          thumbs.forEach(function (o) { o.removeAttribute('aria-current'); });
          t.setAttribute('aria-current', 'true');
        });
      });
    });
  }


  /* --- 8 · Pack picker ---------------------------------------------------
     Fills a 2- or 3-unit pack with whichever devices the shopper wants and
     leaves with that exact basket. Enhancement only: the rung buttons keep
     working permalinks, and this intercepts them just before they navigate,
     so a broken script costs the mix and nothing else. */
  function initPackPicker() {
    var pack = document.querySelector('[data-lp-pack]');
    if (!pack || pack.dataset.lpBound === '1') return;
    pack.dataset.lpBound = '1';

    var unit = parseInt(pack.getAttribute('data-unit'), 10) || 0;
    var code = pack.getAttribute('data-code') || '';
    var rows = [].slice.call(pack.querySelectorAll('[data-lp-pack-row]'));
    var titleEl = pack.querySelector('[data-lp-pack-title]');
    var countEl = pack.querySelector('[data-lp-pack-count]');
    var totalEl = pack.querySelector('[data-lp-pack-total]');
    var noteEl = pack.querySelector('[data-lp-pack-note]');
    var wasEl = pack.querySelector('[data-lp-pack-was]');
    var go = pack.querySelector('[data-lp-pack-go]');
    /* `slots` is what the opener ASKED for, and nothing more. It seeds the
       sheet; it does not lock it. Opening the 3-pack and then deciding two is
       enough used to be impossible — the plus/minus still worked but Continuă
       stayed dead until you were back at exactly three, so the only way to buy
       two from here was to close the sheet and find a different button. The
       offer has always been "2 or 3"; the sheet now says so. */
    var MIN_PACK = 2;
    var MAX_PACK = 3;
    var slots = 3;
    var opener = null;

    function money(c) {
      var w = Math.floor(c / 100), f = c % 100;
      var ws = String(w).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return ws + ',' + (f < 10 ? '0' + f : f) + ' lei';
    }
    // The rules, not the slots: two units cost 1.5 of one, three cost 2.
    function packTotal(k) {
      if (k === 2) return Math.round(unit * 1.5);
      if (k === 3) return unit * 2;
      return unit * k;
    }
    /* ── The pick ORDER, not just the tally ────────────────────────────
       The sheet used to keep only a quantity per row, which is all the
       permalink needs. It is not all the shopper needs: the offer says "a
       treia e gratis" and the sheet then charged a single lump sum with
       nothing marked free, so the discount had to be taken on trust at the
       exact moment trust is being asked for.

       `seq` is the units in the order they were added. The unit that
       completes the pack is the one that gets discounted, which is both the
       honest reading of "the third is free" and the satisfying one — the
       thing you just picked is the thing that goes free, in front of you.
       Which unit is nominally free makes no difference to the total: all
       three devices share one price, and that is the same assumption the
       maths above already rests on. */
    var seq = [];

    function counts() {
      return rows.map(function (_, i) {
        return seq.filter(function (x) { return x === i; }).length;
      });
    }
    function chosen() { return seq.length; }

    function paint() {
      var got = chosen();
      var ready = got >= MIN_PACK;
      var c = counts();
      /* ── Which unit Shopify will actually discount ────────────────────
         Not simply "the one picked last", which is what this was and what
         the note below still claimed. Measured against real carts on 8 sep
         2026: the BXGY takes its free unit from the SMALLEST line — a basket
         of 2 Trimmers + 1 Razor zeroes the Razor whichever order the path is
         written in. Position only decides between lines of EQUAL quantity,
         and there it takes the LAST one in the path (six orderings of a
         three-way mix, six times the last variant).

         So: the smallest line, tie broken by the most recent pick — which is
         still the satisfying reading of "the third is free", the thing you
         just chose going free in front of you — and then that row is written
         LAST in the permalink so the tie-break lands where the sheet says.
         Get this wrong and the sheet shows GRATIS on one device while
         checkout zeroes another: same total, broken promise, on the screen
         where a cash-on-delivery shopper decides whether to trust us. */
      var freeRow = -1;
      if (ready) {
        var minQ = Infinity;
        c.forEach(function (q) { if (q > 0 && q < minQ) minQ = q; });
        for (var si = seq.length - 1; si >= 0; si--) {
          if (c[seq[si]] === minQ) { freeRow = seq[si]; break; }
        }
      }
      // At three the completing unit is free outright; at two it is half price.
      var freeUnit = got >= 3 ? unit : Math.round(unit / 2);

      /* The count line carries the state now that the target is a range, and
         it nudges rather than scolds: at two, the next one is free, and that
         is worth saying on the screen where the shopper is deciding. */
      if (countEl) {
        if (got === 0) countEl.textContent = 'Alege 2 sau 3 aparate';
        else if (got === 1) countEl.textContent = '1 ales · mai alege cel puțin unul';
        else if (got === 2) countEl.textContent = '2 alese · adaugă al treilea și e gratis';
        else countEl.textContent = '3 alese · al treilea e gratis';
      }
      if (totalEl) totalEl.textContent = got > 0 ? money(packTotal(got)) : '\u00a0';
      if (noteEl) {
        noteEl.textContent = got >= 3 ? 'A treia e gratis'
                           : (got === 2 ? 'A doua la jumătate' : '');
      }
      // the undiscounted basket, so the saving is a subtraction the shopper
      // can see rather than a claim they have to accept
      if (wasEl) {
        if (ready) {
          wasEl.innerHTML = '<s>' + money(unit * got) + '</s> economisești ' +
                            money(unit * got - packTotal(got));
        } else {
          wasEl.innerHTML = '&nbsp;';
        }
      }

      rows.forEach(function (r, i) {
        var q = c[i];
        r.querySelector('[data-lp-pack-qty]').textContent = String(q);
        r.querySelector('[data-lp-pack-plus]').disabled = got >= MAX_PACK;
        r.querySelector('[data-lp-pack-minus]').disabled = q <= 0;

        var lineEl = r.querySelector('[data-lp-pack-line]');
        var flagEl = r.querySelector('[data-lp-pack-flag]');
        var isFree = ready && i === freeRow;
        r.setAttribute('data-picked', q > 0 ? 'true' : 'false');
        r.setAttribute('data-free', isFree ? 'true' : 'false');

        if (flagEl) {
          flagEl.textContent = isFree ? (got >= 3 ? 'GRATIS' : '−50%') : '';
          flagEl.hidden = !isFree;
        }
        if (lineEl) {
          if (q <= 0) { lineEl.innerHTML = '&nbsp;'; return; }
          var full = unit * q;
          if (!isFree) { lineEl.textContent = money(full); return; }
          // one unit of this row is discounted, the rest are not
          var after = full - freeUnit;
          lineEl.innerHTML = '<s>' + money(full) + '</s> <b>' + money(after) + '</b>';
        }
      });

      go.setAttribute('aria-disabled', ready ? 'false' : 'true');
      if (ready) {
        /* ── The free line is written LAST ──────────────────────────────
           This used to write it FIRST, on a note claiming that was verified.
           It was not: the shape it had been checked against was `other:1,
           this:2`, where the free unit is decided by the QUANTITY rule and
           position never gets a vote. On the shape this sheet actually
           produces — three lines of one — position is the whole decision,
           and Shopify takes the LAST variant in the path. So the sheet was
           promising GRATIS on one device and zeroing another at checkout.
           See the note on `freeRow` above; re-measure both shapes before
           changing this. */
        var parts = [];
        rows.forEach(function (r, i) {
          if (c[i] > 0 && i !== freeRow) parts.push(r.getAttribute('data-variant') + ':' + c[i]);
        });
        if (freeRow >= 0 && c[freeRow] > 0) {
          parts.push(rows[freeRow].getAttribute('data-variant') + ':' + c[freeRow]);
        }
        var href = '/cart/' + parts.join(',');
        // the code is a minimum-3 rule; attaching it to a 2-pack is noise
        href += (got >= 3 && code) ? ('?discount=' + encodeURIComponent(code) + '&return_to=/checkout')
                                   : '?return_to=/checkout';
        go.setAttribute('href', href);
      } else {
        go.setAttribute('href', '#');
      }
    }

    rows.forEach(function (r, i) {
      r.querySelector('[data-lp-pack-plus]').addEventListener('click', function () {
        if (seq.length >= MAX_PACK) return;
        seq.push(i);
        paint();
      });
      r.querySelector('[data-lp-pack-minus]').addEventListener('click', function () {
        // drop this row's most recent unit, so the order of the rest survives
        for (var k = seq.length - 1; k >= 0; k--) {
          if (seq[k] === i) { seq.splice(k, 1); break; }
        }
        paint();
      });
    });

    function rowOf(variant) {
      var found = -1;
      rows.forEach(function (r, i) {
        if (r.getAttribute('data-variant') === String(variant).trim()) found = i;
      });
      return found;
    }
    function open(k, prefill, trigger) {
      slots = Math.min(Math.max(k, MIN_PACK), MAX_PACK);
      opener = trigger || null;
      /* Neutral title, because the sheet no longer commits to a number the
         moment it opens. Saying "Alege cele 3 aparate" over a sheet that will
         happily sell two is the kind of small lie that makes a shopper stop
         trusting the bigger numbers on the same screen. */
      if (titleEl) titleEl.textContent = 'Alege aparatele';
      seq = [];
      /* Open on something valid, so this is a basket to adjust rather than a
         form to fill. A single variant fills every slot — that is the buy-box
         rung, three of the same. An ARRAY fills the slots in the order given,
         which is how the cross-sell arrives: two of the page's own device
         plus the one whose card was pressed. The order matters, because the
         last seeded unit is the tie-break for `freeRow`, so the device the
         card called free is the device the sheet marks GRATIS. */
      if (prefill) {
        var wanted = [].concat(prefill);
        if (wanted.length === 1) {
          var only = rowOf(wanted[0]);
          if (only >= 0) { for (var j = 0; j < slots; j++) seq.push(only); }
        } else {
          wanted.forEach(function (v) {
            var i = rowOf(v);
            if (i >= 0 && seq.length < MAX_PACK) seq.push(i);
          });
        }
      }
      paint();
      pack.hidden = false;
      document.body.classList.add('ht-noscroll');
      var first = pack.querySelector('[data-lp-pack-plus]');
      if (first) first.focus();
    }
    function close() {
      pack.hidden = true;
      document.body.classList.remove('ht-noscroll');
      if (opener && opener.focus) opener.focus();
    }

    [].slice.call(pack.querySelectorAll('[data-lp-pack-close]')).forEach(function (b) {
      b.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !pack.hidden) close();
    });

    // Rung buttons: intercept only for a real multi-unit rung.
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-lp-pack-open]');
      if (trigger) {
        e.preventDefault();
        var seed = trigger.getAttribute('data-lp-pack-seed');
        open(parseInt(trigger.getAttribute('data-lp-pack-open'), 10) || 3,
             seed ? seed.split(',') : null, trigger);
        return;
      }
      var cta = e.target.closest('[data-lp-checkout]');
      if (!cta) return;
      var scope = cta.closest('[data-lp-offer]');
      if (!scope) return;                       // cross-sell links stay direct
      var checked = scope.querySelector('input[data-lp-qty]:checked');
      if (!checked) return;
      var k = parseInt(checked.getAttribute('data-lp-qty'), 10) || 1;
      if (k < 2) return;                        // a single unit has nothing to pick
      var v = (checked.getAttribute('data-checkout') || '').match(/\/cart\/(\d+):/);
      e.preventDefault();
      open(k, v ? v[1] : null, cta);
    });

    paint();
  }

  /* --- 6 · Header that stays -------------------------------------------
     It used to hide on the way down and come back on the way up. That bought
     a little reading room and cost the one thing the page is for: the button
     back to the offer was absent exactly while the visitor was scrolling
     forward through the argument. The mark and the button are now on screen
     for the whole scroll, on a phone and on a desktop alike — `.lp-hdr` is
     already `position:sticky; top:0`, so this function's only job is to undo
     a hidden state left behind by a cached page or a theme-editor re-render.
     Kept as a named step rather than deleted: the boot list is the inventory
     of what this page does, and a silent gap reads as an oversight. */
  /* ── Add-on sheet ─────────────────────────────────────────────────────
     The last step before checkout: offer the spare blade, then leave for
     checkout whatever the shopper decides.

     Two entry points, because there are two ways to reach a checkout
     permalink on this page:
       1. a single-unit rung — the CTA is a plain permalink and the pack
          picker deliberately ignores it (`if (k < 2) return`);
       2. the pack picker's own "Continuă", whose href is built from the
          slots the shopper filled.
     Both hand this function a finished permalink; it only ever splices one
     more `,<variant>:<qty>` into the PATH, before the query string, so the
     `?discount=` and `return_to=/checkout` that the rung or the pack
     already decided are carried through untouched.

     Registered AFTER initPackPicker so that, on a 2- or 3-unit rung, the
     pack picker's listener runs first and calls preventDefault; the
     `defaultPrevented` guard below then leaves that click alone and the
     sheet opens later, off the pack's own button. Register it earlier and
     both sheets fight over the same click. */
  function initAddon() {
    var box = document.querySelector('[data-lp-add]');
    if (!box || box.dataset.lpBound === '1') return;
    box.dataset.lpBound = '1';

    var variant = box.getAttribute('data-variant');
    var price = parseInt(box.getAttribute('data-price'), 10) || 0;
    var max = parseInt(box.getAttribute('data-max'), 10) || 5;
    var start = parseInt(box.getAttribute('data-start'), 10);
    if (isNaN(start)) start = 1;

    /* ── Which baskets this accessory belongs to ───────────────────────────
       The sheet used to be mounted only on `land-razor`, on the reasoning
       that offering a Razor blade from the Shaver page would be guessing at
       a basket the shopper had not confirmed. That reasoning was right about
       a STATIC section and wrong about this one: by the time this opens, the
       shopper has finished the pack picker and the permalink in hand is the
       exact basket. If a Razor is in it, the blade fits — and it did not
       matter which page the pack was built from.

       So the sheet is now mounted on every LP page and gated on the basket
       instead of on the URL. `data-fits` is the list of variants the
       accessory actually fits; an empty list means "no gate", which keeps
       the snippet usable for an accessory that suits everything. */
    var fits = (box.getAttribute('data-fits') || '')
                 .split(',').map(function (x) { return x.trim(); })
                 .filter(function (x) { return x; });

    function basketVariants(href) {
      var cut = href.indexOf('?');
      var path = cut < 0 ? href : href.slice(0, cut);
      var m = path.match(/\/cart\/(.+)$/);
      if (!m) return [];
      return m[1].split(',').map(function (pair) { return pair.split(':')[0]; });
    }
    function fitsBasket(href) {
      if (!fits.length) return true;
      var have = basketVariants(href);
      return fits.some(function (v) { return have.indexOf(v) !== -1; });
    }

    var qtyEl = box.querySelector('[data-lp-add-qty]');
    var hintEl = box.querySelector('[data-lp-add-hint]');
    var go = box.querySelector('[data-lp-add-go]');
    var goLabel = box.querySelector('[data-lp-add-golabel]');
    var minus = box.querySelector('[data-lp-add-minus]');
    var plus = box.querySelector('[data-lp-add-plus]');

    var baseHref = '';   // the permalink we intercepted
    var baseTotal = 0;   // what that permalink costs, in bani, when known
    var opener = null;

    function money(c) {
      var w = Math.floor(c / 100), f = c % 100;
      return String(w).replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + (f < 10 ? '0' + f : f) + ' lei';
    }
    /* A round threshold is spoken, not printed: "250 de lei", never
       "250,00 lei". In Romanian the "de" is required when the last two
       digits are 00 or 20-99 — 250 de lei, but 21 de lei and 102 lei. */
    function roundMoney(c) {
      var w = Math.round(c / 100);
      var last = w % 100;
      var s = String(w).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return s + (last === 0 || last >= 20 ? ' de lei' : ' lei');
    }
    function qty() { return parseInt(qtyEl.textContent, 10) || 0; }

    /* Splice the add-on into the path, never onto the end of the query —
       `/cart/123:1?discount=X` must become `/cart/123:1,456:2?discount=X`. */
    function withAddon(href, n) {
      if (!n || !variant) return href;
      var cut = href.indexOf('?');
      var path = cut < 0 ? href : href.slice(0, cut);
      var query = cut < 0 ? '' : href.slice(cut);
      return path + ',' + variant + ':' + n + query;
    }

    function paint() {
      var n = qty();
      minus.disabled = n <= 0;
      plus.disabled = n >= max;
      go.setAttribute('href', withAddon(baseHref, n));
      goLabel.textContent = n > 0 ? 'Adaugă și mergi la plată' : 'Mergi la plată';

      if (n <= 0) {
        // never empty: Dawn hides :empty elements outright
        hintEl.innerHTML = '&nbsp;';
        return;
      }
      var line = money(price * n);
      var txt = n + ' × ' + money(price) + ' = ' + line;
      // No free-shipping nudge here any more. Delivery is free from 2
      // APARATE and an accessory is not a device, so adding a blade can
      // never earn it — the old "treci de X lei" line promised the
      // opposite of what the checkout does.
      hintEl.textContent = txt;
    }

    function open(href, total, trigger) {
      baseHref = href || '';
      baseTotal = total || 0;
      opener = trigger || null;
      qtyEl.textContent = String(Math.min(Math.max(start, 0), max));
      paint();
      box.hidden = false;
      document.body.classList.add('ht-noscroll');
      if (plus) plus.focus();
    }

    /* Leaving without the add-on is still leaving FOR checkout: the shopper
       pressed a buy button and that press stands. */
    function skip() {
      var href = baseHref;
      box.hidden = true;
      document.body.classList.remove('ht-noscroll');
      if (href) window.location.href = href;
      else if (opener && opener.focus) opener.focus();
    }

    minus.addEventListener('click', function () { qtyEl.textContent = String(Math.max(0, qty() - 1)); paint(); });
    plus.addEventListener('click', function () { qtyEl.textContent = String(Math.min(max, qty() + 1)); paint(); });

    [].slice.call(box.querySelectorAll('[data-lp-add-skip]')).forEach(function (b) {
      b.addEventListener('click', function (e) { e.preventDefault(); skip(); });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !box.hidden) { e.preventDefault(); skip(); }
    });

    /* Parse the price the rung already displays, so the free-shipping line
       can be honest. `data-price` is the rendered Romanian string
       ("198,88 lei"), which is the only number the buy box exposes. */
    function bani(str) {
      if (!str) return 0;
      var m = String(str).replace(/\s/g, '').match(/([\d.]+),(\d{2})/);
      if (!m) return 0;
      return parseInt(m[1].replace(/\./g, ''), 10) * 100 + parseInt(m[2], 10);
    }

    document.addEventListener('click', function (e) {
      var packGo = e.target.closest('[data-lp-pack-go]');
      if (packGo) {
        if (packGo.getAttribute('aria-disabled') === 'true') return;
        var href = packGo.getAttribute('href') || '';
        if (!href || href === '#') return;
        if (!fitsBasket(href)) {
          // Nothing here takes the accessory, so this click is just a link to
          // checkout — but the pack sheet is still up and the body is still
          // locked. Put both back before letting the navigation happen, so a
          // slow connection does not leave a frozen sheet on screen.
          var p0 = packGo.closest('[data-lp-pack]');
          if (p0) { p0.hidden = true; document.body.classList.remove('ht-noscroll'); }
          return;
        }
        e.preventDefault();
        var pack = packGo.closest('[data-lp-pack]');
        var totalEl = pack && pack.querySelector('[data-lp-pack-total]');
        if (pack) {                       // put the pack sheet away first
          pack.hidden = true;
          document.body.classList.remove('ht-noscroll');
        }
        open(href, bani(totalEl && totalEl.textContent), packGo);
        return;
      }

      var cta = e.target.closest('[data-lp-checkout]');
      if (!cta || e.defaultPrevented) return;   // the pack picker took this one

      /* ── Gate on the BASKET, not on the section ──────────────────────────
         This used to bail out unless the button sat inside `[data-lp-offer]`,
         on the reading that anything else was a cross-sell and should stay
         direct. That was true of `lp-cross` — which is intercepted by the pack
         picker above anyway, and lands here already defaultPrevented — but it
         was wrong about the pair cards: a pair containing the Razor went to
         checkout without ever being offered the blade, and that is the one
         basket the blade exists for.

         Two shapes of button reach this point and only one of them keeps its
         permalink on the anchor:
           · inside [data-lp-offer] — a buy-box rung, where the real permalink
             lives on the checked radio and the anchor's href is only a
             starting value;
           · outside it — a pair card or the sticky bar, whose href IS the
             basket (the sticky bar's is kept in sync by syncSticky).

         Everything past that is decided by `fitsBasket`, so a pair that buys
         nothing the accessory fits is never interrupted. */
      var scope = cta.closest('[data-lp-offer]');
      var checked = scope && scope.querySelector('input[data-lp-qty]:checked');
      var href2 = (checked && checked.getAttribute('data-checkout')) || cta.getAttribute('href') || '';
      if (!href2 || href2 === '#' || href2.indexOf('/cart/') === -1) return;
      if (!fitsBasket(href2)) return;       // this basket takes no accessory
      e.preventDefault();
      open(href2, bani(checked && checked.getAttribute('data-price')), cta);
    });
  }

  function initHeaderPin() {
    var sec = document.querySelector('.lp-hdr-sec') ||
              (document.querySelector('.lp-hdr') || {}).parentNode;
    var hdr = document.querySelector('.lp-hdr');
    if (!hdr) return;
    hdr.classList.remove('is-hidden');
    if (!sec || !sec.classList) return;

    /* ── Pinned, always, on every width ────────────────────────────────────
       16 sep 2026, reversed on David's call. On a phone the header used to
       slide away on the way down and come back on the way up, to give back the
       two rows it costs at 375px. In practice that hid the three tabs
       (Trimmer · Shaver · Razor) and the order button exactly while a visitor
       was reading and comparing. `.lp-hdr-sec{position:sticky;top:0}` already
       does the pinning, so this function no longer moves anything.

       What it still does is MEASURE. A pinned header covers whatever an in-page
       link scrolls to (#oferta, #gama, #cum-functioneaza), and it is 120px on a
       phone (two rows) but 75px from 820px (one row), so the offset has to be
       the height it has right now, not a constant. hottle-lp.css carries a
       per-breakpoint fallback for when this never runs. */
    sec.classList.remove('is-hidden');   // a cached page can arrive in the old hidden state
    var root = document.documentElement;
    function measure() {
      var h = Math.ceil(sec.getBoundingClientRect().height || 0);
      if (h <= 0) return;
      sec.style.setProperty('--lp-hdr-h', h);          // unitless, as before
      root.style.scrollPaddingTop = (h + 12) + 'px';
    }
    window.addEventListener('resize', measure, { passive: true });
    window.addEventListener('load', measure);          // webfonts can change the row height
    // A ResizeObserver reports once as soon as it starts observing, AFTER the
    // browser's own layout. Calling measure() synchronously here as well made
    // the browser lay out the page early, mid-boot, just to answer one height
    // (PageSpeed: "forced reflow" in this function). The CSS fallbacks for
    // scroll-padding cover the single frame before the observer reports.
    if (window.ResizeObserver) new ResizeObserver(measure).observe(sec);
    else measure();
  }

  /* --- 7 · Floating CTA (phone) ------------------------------------------
     Shows once the hero is behind you, hides while the offer is on screen —
     a shortcut that covers the thing it points at is an obstruction — and
     stands down entirely when the add-to-cart bar exists, so the two can
     never stack on top of each other. */
  function initFloatCta() {
    var float = document.querySelector('[data-lp-float]');
    if (!float || float.dataset.lpBound === '1') return;

    // the ATC bar only renders when the product is buyable; it wins
    if (document.querySelector('[data-lp-sticky]')) { float.remove(); return; }

    /* Reparent to <body> before anything else. This element is authored
       inside the header SECTION, and the header wrapper is about to start
       transforming so it can hide on the way down. A transform makes its
       element the containing block for every `position:fixed` descendant —
       which would peel this button off the viewport and glue it to a 56px
       strip at the top of the page. That exact bug has cost this build the
       mobile drawer twice; the fix both times was to reparent, so do it
       here once instead of discovering it a third time. */
    if (float.parentNode !== document.body) document.body.appendChild(float);

    var anchors = [].slice.call(document.querySelectorAll('[data-lp-atc-anchor]'));
    if (!anchors.length || !('IntersectionObserver' in window)) return;
    float.dataset.lpBound = '1';

    var onOffer = false;
    var scrolled = false;

    function apply() {
      float.setAttribute('data-visible', (scrolled && !onOffer) ? 'true' : 'false');
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) onOffer = true; });
      // recompute from scratch: any offer still on screen keeps it hidden
      onOffer = anchors.some(function (a) {
        var r = a.getBoundingClientRect();
        return r.top < window.innerHeight && r.bottom > 0;
      });
      apply();
    }, { threshold: 0 });

    anchors.forEach(function (a) { io.observe(a); });

    window.addEventListener('scroll', function () {
      var was = scrolled;
      scrolled = window.scrollY > 420;
      onOffer = anchors.some(function (a) {
        var r = a.getBoundingClientRect();
        return r.top < window.innerHeight && r.bottom > 0;
      });
      if (was !== scrolled || true) apply();
    }, { passive: true });

    apply();
  }

  /* --- 8 · Scratch card ---------------------------------------------------
     Appears once, after a delay, and only if this browser has not already
     played or dismissed it. The prize is scratched first and the email is
     asked for second: the card is already won by the time the field appears,
     which is the difference between a claim and a toll gate.

     Everything here degrades. No canvas, or reduced motion, and the cover is
     never painted at all: the reveal button in the markup takes over and the
     prize is one click away. The game is decoration; the offer is the point. */
  function initScratch() {
    var root = document.querySelector('[data-lp-scratch]');
    if (!root || root.dataset.lpBound === '1') return;
    root.dataset.lpBound = '1';

    var card = root.querySelector('.lp-scratch__card');
    var canvas = root.querySelector('[data-lp-scratch-canvas]');
    var panel = root.querySelector('[data-lp-scratch-panel]');
    var revealBtn = root.querySelector('[data-lp-scratch-reveal]');
    var hint = root.querySelector('[data-lp-scratch-hint]');
    var steps = {};
    [].slice.call(root.querySelectorAll('[data-lp-scratch-step]')).forEach(function (el) {
      steps[el.getAttribute('data-lp-scratch-step')] = el;
    });

    var KEY = root.getAttribute('data-key') || 'lp-scratch';
    var DAYS = parseInt(root.getAttribute('data-days'), 10) || 30;
    var DELAY = parseInt(root.getAttribute('data-delay'), 10) || 45;
    var THRESHOLD = parseInt(root.getAttribute('data-threshold'), 10) || 55;

    function seen() {
      try {
        var v = window.localStorage.getItem(KEY);
        if (!v) return false;
        return (Date.now() - parseInt(v, 10)) < DAYS * 864e5;
      } catch (e) { return false; }
    }
    function remember() {
      try { window.localStorage.setItem(KEY, String(Date.now())); } catch (e) {}
    }

    function show(name) {
      Object.keys(steps).forEach(function (k) { steps[k].hidden = (k !== name); });
    }

    var lastFocus = null;
    function open(step) {
      lastFocus = document.activeElement;
      root.hidden = false;
      show(step);
      // A tick, not a frame. The card starts at opacity 0 so the transition
      // has somewhere to move from, and `is-open` is what brings it in. A
      // requestAnimationFrame is throttled to nothing in a background tab, so
      // the dialog could open and stay invisible with the page behind it
      // unusable. A timeout still fires there.
      window.setTimeout(function () {
        root.classList.add('is-open');
        var focusable = card.querySelector('input, button, a[href]');
        if (focusable) focusable.focus();
      }, 20);
      document.addEventListener('keydown', onKey);
      if (step === 'play') paint();
    }
    function close() {
      remember();
      root.classList.remove('is-open');
      document.removeEventListener('keydown', onKey);
      window.setTimeout(function () { root.hidden = true; }, REDUCED ? 0 : 320);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
    function onKey(e) {
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      // hold focus inside the dialog while it is open
      var f = [].slice.call(card.querySelectorAll(
        'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'))
        .filter(function (el) { return el.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    [].slice.call(root.querySelectorAll('[data-lp-scratch-close]')).forEach(function (el) {
      el.addEventListener('click', function (ev) {
        // the final button is a real anchor to the offer; let it navigate
        if (el.tagName !== 'A') ev.preventDefault();
        close();
      });
    });

    /* ── the cover ── */
    var ctx = null, painted = false, revealed = false;
    function paint() {
      if (painted || !canvas || REDUCED) { if (!painted && revealBtn) revealBtn.hidden = false; return; }
      try { ctx = canvas.getContext('2d'); } catch (e) { ctx = null; }
      if (!ctx) { revealBtn.hidden = false; return; }

      var r = panel.getBoundingClientRect();
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.round(r.width * dpr));
      canvas.height = Math.max(1, Math.round(r.height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      var g = ctx.createLinearGradient(0, 0, r.width, r.height);
      g.addColorStop(0, '#3A3A42');
      g.addColorStop(0.5, '#25252B');
      g.addColorStop(1, '#3A3A42');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, r.width, r.height);

      var label = canvas.getAttribute('data-cover') || '';
      if (label) {
        ctx.fillStyle = 'rgba(255,255,255,.30)';
        ctx.font = '700 ' + Math.round(Math.min(r.width, r.height) * 0.13) + "px 'Space Grotesk', sans-serif";
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, r.width / 2, r.height / 2);
      }
      painted = true;
    }

    function erase(x, y) {
      if (!ctx) return;
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(x, y, 22, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalCompositeOperation = 'source-over';
    }

    function scratchedPct() {
      if (!ctx) return 0;
      var w = canvas.width, h = canvas.height;
      var data;
      try { data = ctx.getImageData(0, 0, w, h).data; } catch (e) { return 0; }
      var clear = 0, total = 0;
      // every 16th pixel is plenty to know when half of it is gone
      for (var i = 3; i < data.length; i += 4 * 16) {
        total++;
        if (data[i] < 24) clear++;
      }
      return total ? (clear / total) * 100 : 0;
    }

    function reveal() {
      if (revealed) return;
      revealed = true;
      remember();
      if (canvas) canvas.classList.add('is-gone');
      if (hint) hint.hidden = true;
      if (revealBtn) revealBtn.hidden = true;
      window.setTimeout(function () {
        show('claim');
        var input = steps.claim && steps.claim.querySelector('input[type="email"]');
        if (input) input.focus();
      }, REDUCED ? 0 : 500);
    }

    var drawing = false, checks = 0;
    function pos(e) {
      var r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    }
    if (canvas) {
      canvas.addEventListener('pointerdown', function (e) {
        if (revealed) return;
        drawing = true;
        try { canvas.setPointerCapture(e.pointerId); } catch (err) {}
        var p = pos(e); erase(p.x, p.y);
      });
      canvas.addEventListener('pointermove', function (e) {
        if (!drawing || revealed) return;
        var p = pos(e); erase(p.x, p.y);
        if (++checks % 8 === 0 && scratchedPct() >= THRESHOLD) reveal();
      });
      ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (ev) {
        canvas.addEventListener(ev, function () {
          if (!drawing) return;
          drawing = false;
          if (!revealed && scratchedPct() >= THRESHOLD) reveal();
        });
      });
    }
    if (revealBtn) revealBtn.addEventListener('click', reveal);

    /* ── copy the code ── */
    var copyBtn = root.querySelector('[data-lp-scratch-copy]');
    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        var code = copyBtn.getAttribute('data-code') || '';
        var label = copyBtn.querySelector('[data-lp-scratch-copytext]');
        function done() { if (label) { var t = label.textContent; label.textContent = label.getAttribute('data-done') || 'Copiat'; window.setTimeout(function () { label.textContent = t; }, 1800); } }
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(done, function () {});
        } else {
          var ta = document.createElement('textarea');
          ta.value = code; ta.setAttribute('readonly', '');
          ta.style.position = 'fixed'; ta.style.opacity = '0';
          document.body.appendChild(ta); ta.select();
          try { document.execCommand('copy'); done(); } catch (e) {}
          document.body.removeChild(ta);
        }
      });
    }

    /* ── when to appear ── */
    // Shopify reloads the page on form submit, so the won state is rendered
    // server side; if it is there, the visitor just gave us their address and
    // the card reopens on the code rather than making them play again.
    if (root.querySelector('[data-lp-scratch-open]')) { open('done'); return; }
    if (seen()) return;
    window.setTimeout(function () {
      if (!root.hidden) return;
      open('play');
    }, DELAY * 1000);
  }

  /* --- boot -------------------------------------------------------------- */
  function safely(fn, label) {
    try { fn(); } catch (err) {
      if (window.console && console.warn) console.warn('[hottle-lp] ' + label + ' failed', err);
    }
  }

  function boot() {
    safely(initPlayers, 'players');
    safely(initOffers, 'offers');
    safely(initSticky, 'sticky');
    safely(initRails, 'video-rail');
    safely(initShots, 'product-shot');
    safely(initPackPicker, 'pack-picker');
    // AFTER the pack picker, and that order is load-bearing — see initAddon.
    safely(initAddon, 'addon');
    safely(initHeaderPin, 'header-pin');
    safely(initFloatCta, 'float-cta');
    safely(initScratch, 'scratch');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
  // the theme editor re-renders one section at a time
  document.addEventListener('shopify:section:load', boot);
})();
